/**
 * Pre-chat Form Module
 *
 * Handles pre-chat form display, validation, and submission
 */

/**
 * Check if pre-chat form should be shown
 * @param {Object} config - Widget configuration
 * @param {Object} tenantConfig - Tenant configuration
 * @param {Object} context - Context { shopifyContext, savedVisitor, savedSession }
 * @returns {boolean}
 */
export function shouldShowPrechatForm(config, tenantConfig, context = {}) {
    // Check if pre-chat form is enabled in server config
    const formsConfig = tenantConfig?.forms;
    if (!formsConfig || !formsConfig.prechat_enabled) {
        console.log('[FyncallPrechat] Pre-chat form not enabled in config');
        return false;
    }

    // Case 1: Logged-in Shopify customer -> no form needed
    if (context.shopifyContext?.customer?.email) {
        console.log('[FyncallPrechat] Shopify customer logged in, skipping pre-chat form');
        return false;
    }

    // Case 2: Returning visitor with saved info -> no form needed
    if (context.savedVisitor && (context.savedVisitor.email || context.savedVisitor.name)) {
        console.log('[FyncallPrechat] Returning visitor detected, skipping pre-chat form');
        return false;
    }

    // Case 3: Has existing active session -> no form needed
    if (context.savedSession) {
        console.log('[FyncallPrechat] Existing session found, skipping pre-chat form');
        return false;
    }

    // Case 4: Anonymous visitor with pre-chat enabled -> show form
    console.log('[FyncallPrechat] Anonymous visitor, showing pre-chat form');
    return true;
}

/**
 * Render dynamic pre-chat form fields
 * @param {HTMLElement} formContainer - Form container element
 * @param {Array} fields - Field configurations
 * @param {Object} formsConfig - Forms configuration
 */
export function renderDynamicPrechatFields(formContainer, fields, formsConfig) {
    if (!formContainer) return;

    // Clear existing dynamic fields (keep only the form actions)
    const existingFields = formContainer.querySelectorAll('.fyncall-prechat-field');
    existingFields.forEach(f => f.remove());

    // Insert fields before the actions div
    const actionsDiv = formContainer.querySelector('.fyncall-prechat-actions');

    fields.forEach(field => {
        const fieldDiv = document.createElement('div');
        fieldDiv.className = 'fyncall-prechat-field';
        fieldDiv.dataset.fieldId = field.id || field.label.toLowerCase().replace(/\s+/g, '_');

        const label = document.createElement('label');
        label.textContent = field.label;
        if (!field.required) {
            const optional = document.createElement('span');
            optional.className = 'optional';
            optional.textContent = ' (optional)';
            label.appendChild(optional);
        }
        fieldDiv.appendChild(label);

        let input;
        switch (field.type) {
            case 'email':
                input = document.createElement('input');
                input.type = 'email';
                input.placeholder = field.placeholder || 'your@email.com';
                break;
            case 'phone':
                input = document.createElement('input');
                input.type = 'tel';
                input.placeholder = field.placeholder || '+1 (555) 000-0000';
                break;
            case 'dropdown':
                input = document.createElement('select');
                const defaultOpt = document.createElement('option');
                defaultOpt.value = '';
                defaultOpt.textContent = field.placeholder || 'Select an option';
                input.appendChild(defaultOpt);
                (field.options || []).forEach(opt => {
                    const option = document.createElement('option');
                    option.value = opt;
                    option.textContent = opt;
                    input.appendChild(option);
                });
                break;
            case 'textarea':
                input = document.createElement('textarea');
                input.placeholder = field.placeholder || '';
                input.rows = 3;
                break;
            case 'checkbox':
                const checkDiv = document.createElement('div');
                checkDiv.className = 'fyncall-prechat-checkbox';
                input = document.createElement('input');
                input.type = 'checkbox';
                input.id = 'prechat-' + fieldDiv.dataset.fieldId;
                const checkLabel = document.createElement('label');
                checkLabel.htmlFor = input.id;
                checkLabel.textContent = field.placeholder || field.label;
                checkDiv.appendChild(input);
                checkDiv.appendChild(checkLabel);
                fieldDiv.appendChild(checkDiv);
                input = null; // Already appended via checkDiv
                break;
            default: // text
                input = document.createElement('input');
                input.type = 'text';
                input.placeholder = field.placeholder || '';
        }

        if (input) {
            input.dataset.fieldId = fieldDiv.dataset.fieldId;
            input.dataset.required = field.required ? 'true' : 'false';
            fieldDiv.appendChild(input);
        }

        // Error message element
        const errorMsg = document.createElement('span');
        errorMsg.className = 'fyncall-prechat-error';
        errorMsg.textContent = 'This field is required';
        fieldDiv.appendChild(errorMsg);

        formContainer.insertBefore(fieldDiv, actionsDiv);
    });

    // Add department dropdown if department routing is enabled
    if (formsConfig.prechat_department_routing && formsConfig.prechat_departments?.length > 0) {
        const deptDiv = document.createElement('div');
        deptDiv.className = 'fyncall-prechat-field';
        deptDiv.dataset.fieldId = 'department';

        const deptLabel = document.createElement('label');
        deptLabel.textContent = 'Department';
        deptDiv.appendChild(deptLabel);

        const deptSelect = document.createElement('select');
        deptSelect.dataset.fieldId = 'department';
        deptSelect.dataset.required = 'true';

        const defaultDeptOpt = document.createElement('option');
        defaultDeptOpt.value = '';
        defaultDeptOpt.textContent = 'Select a department';
        deptSelect.appendChild(defaultDeptOpt);

        formsConfig.prechat_departments.forEach(dept => {
            const option = document.createElement('option');
            option.value = dept.id || dept.name;
            option.textContent = dept.name;
            deptSelect.appendChild(option);
        });

        deptDiv.appendChild(deptSelect);
        formContainer.insertBefore(deptDiv, actionsDiv);
    }
}

/**
 * Show the pre-chat form
 * @param {Object} elements - DOM elements { prechatForm, messages, inputArea, prechatContinue }
 * @param {Object} formsConfig - Forms configuration
 */
export function showPrechatForm(elements, formsConfig) {
    // Update form header if configured
    const headerTitle = elements.prechatForm.querySelector('.fyncall-prechat-header h3');
    const headerSubtitle = elements.prechatForm.querySelector('.fyncall-prechat-header p');
    if (headerTitle) {
        headerTitle.textContent = formsConfig?.prechat_title || 'Before we begin';
    }
    if (headerSubtitle) {
        headerSubtitle.textContent = formsConfig?.prechat_subtitle || 'Please provide some information to help us assist you better.';
    }

    // Update continue button text
    if (elements.prechatContinue && formsConfig?.prechat_submit_text) {
        elements.prechatContinue.childNodes[0].textContent = formsConfig.prechat_submit_text + ' ';
    }

    // Render dynamic fields if configured
    if (formsConfig?.prechat_fields && formsConfig.prechat_fields.length > 0) {
        const formContainer = elements.prechatForm.querySelector('.fyncall-prechat-form');
        renderDynamicPrechatFields(formContainer, formsConfig.prechat_fields, formsConfig);
    }

    elements.prechatForm.classList.remove('hidden');
    elements.messages.style.display = 'none';
    if (elements.inputArea) {
        elements.inputArea.style.display = 'none';
    }

    // Focus the first input
    const firstInput = elements.prechatForm.querySelector('input, select, textarea');
    if (firstInput) firstInput.focus();
}

/**
 * Hide the pre-chat form
 * @param {Object} elements - DOM elements { prechatForm, messages, inputArea }
 */
export function hidePrechatForm(elements) {
    elements.prechatForm.classList.add('hidden');
    elements.messages.style.display = '';
    if (elements.inputArea) {
        elements.inputArea.style.display = '';
    }
}

/**
 * Validate and collect pre-chat form data
 * @param {HTMLElement} formContainer - Form container element
 * @param {Object} legacyElements - Legacy input elements { prechatName, prechatEmail }
 * @returns {{ formData: Object, hasErrors: boolean }}
 */
export function validatePrechatForm(formContainer, legacyElements = {}) {
    const formData = {};
    let hasErrors = false;

    // Get all dynamic fields
    const fields = formContainer.querySelectorAll('.fyncall-prechat-field');
    fields.forEach(fieldDiv => {
        const input = fieldDiv.querySelector('input, select, textarea');
        if (!input) return;

        const fieldId = input.dataset.fieldId;
        const required = input.dataset.required === 'true';
        let value;

        if (input.type === 'checkbox') {
            value = input.checked;
        } else {
            value = input.value.trim();
        }

        // Validate required fields
        if (required && !value) {
            fieldDiv.classList.add('error');
            hasErrors = true;
        } else {
            fieldDiv.classList.remove('error');
        }

        formData[fieldId] = value;
    });

    // Also check legacy name/email inputs if they exist and no dynamic fields
    if (fields.length === 0) {
        const name = legacyElements.prechatName?.value?.trim();
        const email = legacyElements.prechatEmail?.value?.trim();
        if (name) formData.name = name;
        if (email) formData.email = email;
    }

    return { formData, hasErrors };
}

/**
 * Handle pre-chat form submission
 * @param {HTMLElement} formContainer - Form container element
 * @param {Object} legacyElements - Legacy input elements
 * @param {Object} callbacks - Callbacks { onSuccess, onSaveVisitor }
 * @returns {Object|null} Form data if valid, null if errors
 */
export function handlePrechatSubmit(formContainer, legacyElements, callbacks = {}) {
    const { formData, hasErrors } = validatePrechatForm(formContainer, legacyElements);

    if (hasErrors) {
        console.log('[FyncallPrechat] Pre-chat form validation failed');
        return null;
    }

    // Save visitor info
    if (formData.name || formData.email) {
        callbacks.onSaveVisitor?.({
            name: formData.name || '',
            email: formData.email || ''
        });
    }

    console.log('[FyncallPrechat] Pre-chat form data collected:', formData);
    callbacks.onSuccess?.(formData);

    return formData;
}
